import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {Helmet} from "react-helmet";

function Subscription() {
  // Define the comparison data for each subscription feature
  const rows = [
    { feature: "Monthly Cost", bronze: "$29.99", gold: "$49.99", platinum: "$69.99" },
    { feature: "Placement in search results", bronze: true, gold: true, platinum: true },
    { feature: "Number of ads", bronze: true, gold: true, platinum: true },
    { feature: "Number of photos", bronze: true, gold: true, platinum: true },
    { feature: "Be in the weekly newsletter", bronze: true, gold: true, platinum: true },
    { feature: "Cancel anytime", bronze: true, gold: true, platinum: true },
    { feature: "AI-driven photo generation", bronze: false, gold: true, platinum: true },
    { feature: "AI-driven text generation", bronze: false, gold: true, platinum: true },
    { feature: "Offer special deals or coupon", bronze: false, gold: true, platinum: true },
    { feature: "Calendar for reservation", bronze: false, gold: false, platinum: true },
    { feature: "Payment for reservation", bronze: false, gold: false, platinum: true },
    { feature: "Background checks of clients", bronze: false, gold: false, platinum: true },
  ];

  // State for dialog open/close, selected plan, and email value
  const [open, setOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  // Regex for basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // On component mount, read the query string for an emailAddress parameter
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const emailParam = query.get('emailAddress');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, []);

  // Open dialog when a subscription button is clicked
  const handleSubscribe = (plan) => {
    setSelectedPlan(plan);
    setOpen(true);
  };

  // Close the dialog
  const handleClose = () => {
    setOpen(false);
    setSelectedPlan('');
  };

  // Save subscription action (add your own subscription logic)
  const handleSave = () => {
    if (email) {
      console.log(`Subscribed ${email} to the ${selectedPlan} plan.`);
      // Insert additional subscription handling logic here
      handleClose();
    }
  };

  // Handler for email change with validation
  const handleEmailChange = (event) => {
    const newEmail = event.target.value;
    setEmail(newEmail);
    if (newEmail && !emailRegex.test(newEmail)) {
      setEmailError('Please enter a valid email address.');
    } else {
      setEmailError('');
    }
  };

  // Utility function to render a checkmark for boolean values
  const renderValue = (value) => {
    if (typeof value === 'boolean') {
      return value ? '✓' : '—';
    }
    return value;
  };

  // Define semi-transparent colors for the subscription columns
  const bronzeColor = 'rgba(250,214,165, 0.6)';   // Light bronze with transparency
  const goldColor = 'rgba(255,249,196, 0.6)';       // Light gold with transparency
  const platinumColor = 'rgba(224,224,224, 0.6)';    // Light platinum with transparency

  // Border style for table cells
  const cellBorder = '1px solid #ccc';

  return (
    <Box className="content" sx={{ padding: '20px' }}>
      <Helmet>
        <title>PairCare - Subscription</title>
      </Helmet>
      {/* Inline style for page margins */}
      <style>{`
        .content {
          min-height: calc(100vh - 249px);
          margin-bottom: 1in;
        }
      `}</style>
      
      <Typography variant="h4" align="center" gutterBottom>
        Compare Subscription Plans
      </Typography>
      
      <Box sx={{ maxWidth: 'calc(100vw - 40px)' }}>
        <TableContainer component={Paper} sx={{overflowX: 'auto'}}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ border: cellBorder }}>Features</TableCell>
                <TableCell align="center" sx={{ backgroundColor: bronzeColor, border: cellBorder }}>
                  Bronze
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: goldColor, border: cellBorder }}>
                  Gold
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: platinumColor, border: cellBorder }}>
                  Platinum
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell component="th" scope="row" sx={{ border: cellBorder }}>
                    {row.feature}
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: bronzeColor, border: cellBorder }}>
                    {renderValue(row.bronze)}
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: goldColor, border: cellBorder }}>
                    {renderValue(row.gold)}
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: platinumColor, border: cellBorder }}>
                    {renderValue(row.platinum)}
                  </TableCell>
                </TableRow>
              ))}
              {/* Row containing subscribe buttons */}
              <TableRow>
                <TableCell sx={{ border: cellBorder }}>
                  <strong>Subscribe</strong>
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: bronzeColor, border: cellBorder }}>
                  <Button variant="contained" color="primary" onClick={() => handleSubscribe('Bronze')}>
                    Choose Bronze
                  </Button>
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: goldColor, border: cellBorder }}>
                  <Button variant="contained" color="primary" onClick={() => handleSubscribe('Gold')}>
                    Choose Gold
                  </Button>
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: platinumColor, border: cellBorder }}>
                  <Button variant="contained" color="primary" onClick={() => handleSubscribe('Platinum')}>
                    Choose Platinum
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      
      {/* Dialog for email subscription */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Subscribe to {selectedPlan} Plan</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="standard"
            value={email}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={emailError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={!email}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Subscription;

